/*
    Merrid Controls. Wszelkie prawa zastrzeżone.
    Plik zawiera kody Źródłowe będące własnością firmy Merrid Controls.
    Nie wolno ich rozpowszechniać w całości lub fragmentach bez wyraźnej zgody właściciela.
*/


import React, {useState, useEffect, useContext} from "react";


import {Col, Row} from "react-bootstrap";


import MONITORY_NOTE_INTERFACE from "@Services/Application/Interfaces/MonitoryNotes/MonitoryNote";
import PRODUCT_INTERFACE from "@Services/Application/Interfaces/Products/Product";
import dateFormat from "dateformat";
import {AiOutlineCheck} from "react-icons/all";
import DISPOSITION_INTERFACE from "@Services/Application/Interfaces/Dispositions/Disposition";
import ERROR_SYSTEM_MESSAGE_INTERFACE from "@Services/Application/Interfaces/_Messages/Errors";
import ErrorMessageComponent from "@Components/UI/Communicates/Messages/Error";
import LOGGED_USER_INTERFACE from "@Store/Interfaces/Authentication/User";
import useTypedSelector from "@Services/Hooks/UseTypedSelector";
import ApplicationApiContext from "@Contexts/Api";
import AUTHORIZATION_TOKEN_INTERFACE from "@Reducers/Api/RequestToken";
import REST_API_REQUEST from "@Services/Utilities/Requests";
import {HTTP_ERROR_RESPONSE_INTERFACE} from "@Services/Interfaces/Http/Response/Error";


type MonitoryNoteDispositionsProps = {
    MONITORY_NOTE: MONITORY_NOTE_INTERFACE | null;
    MONITORY_NOTE_PROCESS_TYPE: "edition" | "delete" | "submit" | "create";
    LOAD_CONTENT: boolean;
    LOAD_CONTENT_HANDLER(status: boolean): void;
    removeDispositionsHandler?(disposition: DISPOSITION_INTERFACE, remove: boolean): void;
};

export const MonitoryNoteDispositionsComponent: React.FC<MonitoryNoteDispositionsProps> = ({
    MONITORY_NOTE = null, MONITORY_NOTE_PROCESS_TYPE,
    LOAD_CONTENT, LOAD_CONTENT_HANDLER,
    removeDispositionsHandler
}): JSX.Element | null => {


    const LOGGED_USER: LOGGED_USER_INTERFACE = useTypedSelector(
        (state):ReturnType<any> => state?.authentication
    )?.loggedUser || null;


    const REST_API_URL = useContext(ApplicationApiContext)?.API_ROOT_URL || null;


    /** @const {object} REST_API_DATA - Application State Store Rest Api Data */
    const REST_API_DATA:ReturnType<any> = useTypedSelector(
        (state) => state.api
    );

    /** @const {} REST_API_REQUEST_TOKEN - Application Rest Api Request Token */
    const REST_API_REQUEST_TOKEN: AUTHORIZATION_TOKEN_INTERFACE = REST_API_DATA?.requestToken || null;


    const [showDispositions, setShowDispositions] = useState<boolean>(!!(MONITORY_NOTE_PROCESS_TYPE === "delete" || MONITORY_NOTE_PROCESS_TYPE === "submit"));

    const [availableDispositions, setAvailableDispositions] = useState<DISPOSITION_INTERFACE[] | null>(null);
    const [availableDispositionsError, setAvailableDispositionsError] = useState<ERROR_SYSTEM_MESSAGE_INTERFACE | null>(null);

    const selectedFrontVehicle = MONITORY_NOTE?.FrontTruckRegNo;
    const selectedRearVehicle = MONITORY_NOTE?.RearTruckRegNo;

    useEffect(() => {

        if (!selectedFrontVehicle) return;

        if (!MONITORY_NOTE) return;

        const dispositionsListApiURL = REST_API_URL + "/Disp/List";

        const DISPOSITIONS_LIST_REQUEST_DATA = {
            UserId: LOGGED_USER?.userID,
            TerminalNo: MONITORY_NOTE.TerminalNo,
            FrontVehicleRegNo: selectedFrontVehicle || null,
            RearVehicleRegNo: selectedRearVehicle || null
        };

        const getAvailableDispositions = async () => {

            try {

                const DISPOSITIONS_RESPONSE = await REST_API_REQUEST(
                    dispositionsListApiURL,
                    DISPOSITIONS_LIST_REQUEST_DATA,
                    REST_API_REQUEST_TOKEN
                );

                if (DISPOSITIONS_RESPONSE.status === 200) {
                    const CONTENT: string = await DISPOSITIONS_RESPONSE.text();
                    setAvailableDispositions(CONTENT.trim() ? JSON.parse(CONTENT)?.Disps : []);
                } else {
                    const CONTENT: HTTP_ERROR_RESPONSE_INTERFACE = await DISPOSITIONS_RESPONSE.json();
                    setAvailableDispositionsError({ErrorCode: DISPOSITIONS_RESPONSE.status, UserMessage: CONTENT.UserMessage});
                }

            } catch (e) {
                setAvailableDispositionsError({ErrorCode: 500, UserMessage: "Wystąpił nieokreślony błąd - skontaktuj się z CDOK"});
            }

            if (!!LOAD_CONTENT_HANDLER) LOAD_CONTENT_HANDLER(true);

        };

        getAvailableDispositions();

    }, [selectedFrontVehicle, selectedRearVehicle]);

    useEffect(() => {
        return () => {
            setAvailableDispositions(null);
            setAvailableDispositionsError(null);
        };
    }, []);


    if (!MONITORY_NOTE || !MONITORY_NOTE?.Disps) return null;


    if (!!availableDispositionsError) return <ErrorMessageComponent messageText={availableDispositionsError.UserMessage} />


    if (!LOAD_CONTENT) return null;


    if (!availableDispositions) return null;


    const selectedDispositions = MONITORY_NOTE?.Disps.map((disposition) => disposition.DispNo);


    const DispositionsList = availableDispositions.map(
        (disposition) => {

            let dispositionSentNumbers = null;

            if (disposition.DispItems) {
                dispositionSentNumbers = disposition.DispItems.filter(
                    (product: PRODUCT_INTERFACE) => !!product.SENTNumber
                ).map(
                    (product: PRODUCT_INTERFACE) => product.SENTNumber
                )
                dispositionSentNumbers = Array.from(new Set(dispositionSentNumbers));
            }

            const dispositionSelectedStatus = selectedDispositions.includes(disposition.DispNo);


            if (!["create", "edition"].includes(MONITORY_NOTE_PROCESS_TYPE) && !dispositionSelectedStatus) return null;

            return (
                <Col key={disposition.DispNo} xs={12} className={"monitory-note-disposition"}>
                    <Row className={"monitory-note-disposition-content"}>

                        <Col xs={1} className={"disposition-selected-status"}>
                            <Row className={"disposition-selected-status-content"}>
                                <span className={"disposition-selected-icon" + (MONITORY_NOTE_PROCESS_TYPE === "delete" || MONITORY_NOTE_PROCESS_TYPE === "submit" ? " inactive" : "")} onClick={() => removeDispositionsHandler ? removeDispositionsHandler(disposition, dispositionSelectedStatus) : null}>
                                    {!!dispositionSelectedStatus && <AiOutlineCheck />}
                                </span>
                            </Row>
                        </Col>

                        <Col xs={{span: 10, offset: 1}} className={"disposition-information"}>
                            <Row className={"disposition-information-content"}>

                                {!!disposition.DispNo &&
	                            <Col xs={12} className={"disposition-id"}>
		                            <Row className={"disposition-id-content"}>
                                        <Col xs={6} className={"disposition-id-value"}>
	                                        <span>{disposition.DispNo}</span>
                                        </Col>
                                        {!!dispositionSentNumbers && dispositionSentNumbers?.length > 0 &&
			                            <Col xs={6} className={"disposition-sent-nr"}>
                                            {dispositionSentNumbers.join("<br/>")}
			                            </Col>
                                        }
                                    </Row>
	                            </Col>
                                }

                                <Col xs={12} className={"disposition-dates"}>
                                    {!!disposition.ValidFrom && <span className={"info-label"}>{`od`}</span>}
                                    {!!disposition.ValidFrom &&
                                    <span className={"disposition-start-date"}>{dateFormat(new Date(disposition.ValidFrom), "dd-mm-yyyy")}</span>
                                    }
                                    {!!disposition.ValidTo && <span className={"info-label"}>{`do`}</span>}
                                    {!!disposition.ValidTo &&
                                    <span className={"disposition-end-date"}>{dateFormat(new Date(disposition.ValidTo), "dd-mm-yyyy")}</span>
                                    }
                                </Col>

                            </Row>
                        </Col>

                    </Row>
                </Col>
            );
        }
    );


    return (
        <Col xs={12} className={"monitory-note-dispositions-component monitory-note-dispositions"}>
            <Row className={"monitory-note-dispositions-content"}>

                <Col xs={12} as={"h4"} className={"monitory-note-dispositions-header"}>
                    <span className={"checkbox" + (MONITORY_NOTE_PROCESS_TYPE === "delete" || MONITORY_NOTE_PROCESS_TYPE === "submit" ? " inactive" : "")} onClick={() => MONITORY_NOTE_PROCESS_TYPE === "delete" || MONITORY_NOTE_PROCESS_TYPE === "submit" ? null : setShowDispositions(!showDispositions)}>
                        {!!showDispositions && <AiOutlineCheck />}
                    </span>
                    <span>{`Pokaż Dyspozycje`}</span>
                </Col>

                {!!showDispositions && DispositionsList}

            </Row>
        </Col>
    );

};


export default MonitoryNoteDispositionsComponent;
